import { useEffect, useMemo, useState } from 'react';
import { useDataTable, useDataTableExpansion } from '@odo/shared-ui';
import { usePracticeUsers, useInfinitePracticeUsers } from '../../../../hooks/usePracticeUsers';
import { useToast } from '../../../../providers/ToastProvider';
import {
    PRACTICE_USERS_MAX_LIMIT,
    PRACTICE_USERS_SEARCH_MAX_LENGTH,
    type PracticeUserRow
} from '../../../../services/practiceUsers';
import { apiErrorMessage } from '../../../../utils/apiError';
import { UserDrawerHost } from '../../users/UserDrawerHost';
import { useUserDrawerController } from '../../users/useUserDrawerController';
import { AddPracticeUserModal } from './AddPracticeUserModal';
import { PracticeUsersTable } from './PracticeUsersTable';
import { RemoveUserModal } from './RemoveUserModal';

const SEARCH_DEBOUNCE_MS = 300;

interface PracticeUsersTabProps {
    practiceId: string;
    practiceName: string;
}

// The table owns its own toolbar (search + "Invite user", opening
// AddPracticeUserModal) and loading/empty states - this tab just wires data and modals.
export function PracticeUsersTab({ practiceId, practiceName }: PracticeUsersTabProps) {
    const { showToast } = useToast();
    const [showAddUser, setShowAddUser] = useState(false);
    const [removeTarget, setRemoveTarget] = useState<PracticeUserRow | null>(null);
    const [searchInput, setSearchInput] = useState('');

    // No sortable columns - the unsorted overload. Debouncing stays here: the
    // hook holds committed state only, and `setSearch` resets the page for us.
    const table = useDataTable({});
    const expansion = useDataTableExpansion();
    const { setSearch } = table;
    useEffect(() => {
        const handle = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [searchInput, setSearch]);

    const isInfinite = table.mode === 'infinite';
    const search = table.search || undefined;

    // Both hooks are called unconditionally and only one is ever `enabled` -
    // same split as InvoicesContent's paged/infinite pair.
    const paged = usePracticeUsers(practiceId, {
        page: table.page,
        limit: table.limit,
        search,
        enabled: !isInfinite
    });
    const infinite = useInfinitePracticeUsers(practiceId, {
        limit: table.limit,
        search,
        enabled: isInfinite
    });

    const users = isInfinite ? infinite.users : paged.users;
    const meta = isInfinite ? infinite.meta : paged.meta;
    const isLoading = isInfinite ? infinite.isLoading : paged.isLoading;
    const isError = isInfinite ? infinite.isError : paged.isError;
    const error = isInfinite ? infinite.error : paged.error;

    // ExistingUserPane's dedupe needs every linked email, not just the current
    // page - a second, unfiltered fetch (capped at max `limit`) runs only while the modal is open.
    const membership = usePracticeUsers(practiceId, {
        page: 1,
        limit: PRACTICE_USERS_MAX_LIMIT,
        enabled: showAddUser
    });
    const existingEmails = useMemo(
        () => new Set(membership.users.map((user) => user.email)),
        [membership.users]
    );

    // Rows open the same UserDrawer as Accounts > Users via the shared controller -
    // no create mode (invites use AddPracticeUserModal), always deep-link fetches by email.
    const drawer = useUserDrawerController({ allowCreate: false });
    const openUser = (user: PracticeUserRow) => drawer.openEditDrawer(user.email);

    useEffect(() => {
        if (isError) {
            showToast(apiErrorMessage(error, 'Failed to load users'), 'error');
        }
    }, [isError, error, showToast]);

    const totalPages = Math.max(1, Math.ceil(meta.total / table.limit));

    return (
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col gap-4">
            <PracticeUsersTable
                users={users}
                isLoading={isLoading}
                isSearching={Boolean(search)}
                search={{
                    value: searchInput,
                    onChange: setSearchInput,
                    placeholder: 'Search users',
                    maxLength: PRACTICE_USERS_SEARCH_MAX_LENGTH
                }}
                mode={table.mode}
                onModeChange={table.setMode}
                pagination={{
                    page: table.page,
                    totalPages,
                    total: meta.total,
                    onPage: table.setPage
                }}
                onLoadMore={infinite.fetchNextPage}
                hasMore={infinite.hasNextPage}
                isLoadingMore={infinite.isFetchingNextPage}
                onInvite={() => setShowAddUser(true)}
                onOpenUser={openUser}
                onRemove={setRemoveTarget}
                expansion={expansion}
            />

            {showAddUser && (
                <AddPracticeUserModal
                    practiceId={practiceId}
                    practiceName={practiceName}
                    existingEmails={existingEmails}
                    onClose={() => setShowAddUser(false)}
                />
            )}

            {removeTarget && (
                <RemoveUserModal
                    practiceId={practiceId}
                    practiceName={practiceName}
                    user={removeTarget}
                    onClose={() => setRemoveTarget(null)}
                />
            )}

            <UserDrawerHost controller={drawer} />
        </div>
    );
}
